import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { TourCard } from '@/components/domain/TourCard';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Input } from '@/components/ui/Input';
import { IcPlane, IcSearch } from '@/components/ui/icons';
import { useTours } from '@/api/queries/tour';
import { paths } from '@/routes/paths';
import { useUIStore } from '@/store/ui';

type Tab = 'tickets' | 'taxi' | 'hotel';

export function TravelListPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('tickets');
  const [filter, setFilter] = useState<'local' | 'international' | undefined>(undefined);
  const tours = useTours({ type: filter, limit: 12 });
  const pushToast = useUIStore((s) => s.pushToast);

  const handleTab = (t1: Tab) => {
    setTab(t1);
    if (t1 === 'tickets') navigate(paths.travelFlightSearch);
    else pushToast({ body: t('common.coming_soon'), variant: 'info' });
  };

  return (
    <AppShell>
      <PageContainer>
        <TopBar bell title={t('travel.title')} onBellClick={() => navigate(paths.notifications)} />
        <Input
          startAdornment={<IcSearch />}
          placeholder={t('common.search_placeholder')}
          aria-label={t('common.search')}
        />

        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant={tab === 'tickets' ? 'primary' : 'secondary'} onClick={() => handleTab('tickets')} startIcon={<IcPlane />}>
            {t('travel.tabs.buy_tickets')}
          </Button>
          <Button variant={tab === 'taxi' ? 'primary' : 'secondary'} onClick={() => handleTab('taxi')}>
            {t('travel.tabs.call_taxi')}
          </Button>
          <Button variant={tab === 'hotel' ? 'primary' : 'secondary'} onClick={() => handleTab('hotel')}>
            {t('travel.tabs.book_hotel')}
          </Button>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            size="sm"
            variant={filter === undefined ? 'primary' : 'secondary'}
            onClick={() => setFilter(undefined)}
          >
            {t('common.all')}
          </Button>
          <Button
            size="sm"
            variant={filter === 'local' ? 'primary' : 'secondary'}
            onClick={() => setFilter('local')}
          >
            Local
          </Button>
          <Button
            size="sm"
            variant={filter === 'international' ? 'primary' : 'secondary'}
            onClick={() => setFilter('international')}
          >
            Intl
          </Button>
        </div>

        <h3 style={{ marginBlock: 8 }}>{t('travel.popular_tours')}</h3>
        {tours.isLoading && <Spinner />}
        {!tours.isLoading && !tours.data?.items?.length && (
          <Card variant="light"><p style={{ margin: 0 }}>{t('common.coming_soon')}</p></Card>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {tours.data?.items?.map((tour) => (
            <TourCard key={tour.id} tour={tour} onClick={() => navigate(paths.travelDetail(tour.id))} />
          ))}
        </div>
      </PageContainer>
    </AppShell>
  );
}
